import { useEffect, useRef, useState } from 'react';
import { useAuth, customerSupabase } from './AuthContext';

const readLocalList = (storageKey) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

const getRemovedStorageKey = (storageKey, userId) => `${storageKey}_removed_${userId || 'guest'}`;

const readRemovedIds = (key) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) || '[]');
    return new Set(Array.isArray(saved) ? saved : []);
  } catch {
    return new Set();
  }
};

const filterRemovedItems = (items, removedIds) => items.filter((item) => !removedIds.has(item.id));

const mergeLists = (localItems, accountItems, listType) => {
  const merged = new Map(accountItems.map((item) => [item.id, item]));
  localItems.forEach((item) => {
    const saved = merged.get(item.id);
    merged.set(item.id, listType === 'cart'
      ? { ...saved, ...item, quantity: Math.max(saved?.quantity || 0, item.quantity || 1) }
      : item);
  });
  return [...merged.values()];
};

export const useSavedList = (storageKey, listType) => {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(() => readLocalList(storageKey));
  const activeUserId = useRef(null);
  const hydrated = useRef(false);
  const getCurrentRemovedStorageKey = () => getRemovedStorageKey(storageKey, activeUserId.current || user?.id);

  const removeSavedItems = (ids) => {
    const removedIds = readRemovedIds(getCurrentRemovedStorageKey());
    ids.forEach((id) => removedIds.add(id));
    window.localStorage.setItem(getCurrentRemovedStorageKey(), JSON.stringify([...removedIds]));
    setItems((current) => filterRemovedItems(current, new Set(ids)));
  };

  const restoreSavedItems = (ids) => {
    const key = getCurrentRemovedStorageKey();
    const removedIds = readRemovedIds(key);
    ids.forEach((id) => removedIds.delete(id));
    window.localStorage.setItem(key, JSON.stringify([...removedIds]));
  };

  useEffect(() => {
    if (loading) return undefined;
    let mounted = true;

    const loadItems = async () => {
      if (!user) {
        if (activeUserId.current) {
          window.localStorage.removeItem(storageKey);
          setItems([]);
        } else {
          setItems(filterRemovedItems(readLocalList(storageKey), readRemovedIds(getRemovedStorageKey(storageKey, null))));
        }
        activeUserId.current = null;
        hydrated.current = true;
        return;
      }

      const { data, error } = await customerSupabase
        .from('customer_saved_lists')
        .select('items')
        .eq('user_id', user.id)
        .eq('list_type', listType)
        .maybeSingle();
      if (!mounted) return;

      if (error) {
        activeUserId.current = user.id;
        setItems((current) => filterRemovedItems(current, readRemovedIds(getRemovedStorageKey(storageKey, user.id))));
        hydrated.current = true;
        return;
      }

      const localItems = readLocalList(storageKey);
      const accountItems = Array.isArray(data?.items) ? data.items : [];
      const mergedItems = mergeLists(localItems, accountItems, listType);
      const nextItems = filterRemovedItems(mergedItems, readRemovedIds(getRemovedStorageKey(storageKey, user.id)));
      setItems(nextItems);
      activeUserId.current = user.id;
      hydrated.current = true;
      if (nextItems.length && !data) {
        await customerSupabase.from('customer_saved_lists').upsert({
          user_id: user.id,
          list_type: listType,
          items: nextItems,
        });
      }
    };

    loadItems();
    return () => { mounted = false; };
  }, [user, loading, storageKey, listType]);

  useEffect(() => {
    if (!hydrated.current) return;
    window.localStorage.setItem(storageKey, JSON.stringify(items));
    if (!loading && user && activeUserId.current === user.id) {
      customerSupabase.from('customer_saved_lists').upsert({
        user_id: user.id,
        list_type: listType,
        items,
      });
    }
  }, [items, loading, listType, storageKey, user]);

  return [items, setItems, { removeSavedItems, restoreSavedItems }];
};